import React from 'react';
import styled, { css } from 'styled-components';

/**
 * Escape HTML, then apply light formatting for plain-text product descriptions.
 * If the string already looks like HTML (from TinyMCE), sanitize lightly and keep markup + styles.
 */
export function formatProductDescriptionHtml(raw) {
  const text = String(raw || '').replace(/\r\n/g, '\n').trim();
  if (!text) return '';

  const looksLikeHtml = /<[a-z][\s\S]*>/i.test(text);
  if (looksLikeHtml) {
    return text
      .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
      .replace(/<iframe[\s\S]*?>[\s\S]*?<\/iframe>/gi, '')
      .replace(/<object[\s\S]*?>[\s\S]*?<\/object>/gi, '')
      .replace(/<embed[\s\S]*?>/gi, '')
      .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
      .replace(/javascript:/gi, '')
      .replace(/expression\s*\(/gi, '');
  }

  const escape = (s) =>
    String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const inline = (s) =>
    escape(s).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  const isBulletLine = (l) => /^\s*[-*•]\s+/.test(l);
  const bulletText = (l) => l.replace(/^\s*[-*•]\s+/, '').trim();

  function tokenizeLine(line) {
    const trimmed = line.trimEnd();
    if (!trimmed.trim()) return [{ type: 'blank' }];
    if (isBulletLine(trimmed)) {
      return [{ type: 'bullet', text: bulletText(trimmed) }];
    }
    if (trimmed.includes('•')) {
      const parts = trimmed
        .split(/\s*•\s*/)
        .map((p) => p.trim())
        .filter(Boolean);
      if (parts.length >= 2) {
        const startsWithBullet = /^\s*•/.test(trimmed);
        if (startsWithBullet) {
          return parts.map((text) => ({ type: 'bullet', text }));
        }
        return [
          { type: 'para', text: parts[0] },
          ...parts.slice(1).map((text) => ({ type: 'bullet', text })),
        ];
      }
    }
    return [{ type: 'para', text: trimmed.trim() }];
  }

  const tokens = text.split('\n').flatMap(tokenizeLine);

  const htmlParts = [];
  let paraBuf = [];
  let bulletBuf = [];

  const flushPara = () => {
    if (!paraBuf.length) return;
    htmlParts.push(`<p>${paraBuf.map((t) => inline(t)).join('<br />')}</p>`);
    paraBuf = [];
  };
  const flushBullets = () => {
    if (!bulletBuf.length) return;
    htmlParts.push(
      `<ul>${bulletBuf.map((t) => `<li>${inline(t)}</li>`).join('')}</ul>`
    );
    bulletBuf = [];
  };

  for (const tok of tokens) {
    if (tok.type === 'blank') {
      flushPara();
      flushBullets();
      continue;
    }
    if (tok.type === 'bullet') {
      flushPara();
      bulletBuf.push(tok.text);
      continue;
    }
    flushBullets();
    paraBuf.push(tok.text);
  }
  flushPara();
  flushBullets();

  return htmlParts.join('');
}

/** Plain-text length for clamp / “show more” decisions (ignores HTML tags). */
export function productDescriptionPlainLength(raw) {
  const html = formatProductDescriptionHtml(raw);
  return html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim().length;
}

/**
 * Convert TinyMCE absolute font sizes into a card-safe px range so desktop
 * package grids don’t blow up, while keeping relative hierarchy.
 */
export function normalizeFontsForCard(html) {
  if (!html) return html;
  return html.replace(/font-size\s*:\s*([\d.]+)\s*(pt|px|em|rem)/gi, (_full, num, unit) => {
    let px = Number(num);
    const u = String(unit).toLowerCase();
    if (u === 'pt') px *= 96 / 72;
    else if (u === 'em' || u === 'rem') px *= 14;
    // Soft map: 12px→12, 18px→13.5, 24px→14.5, 32px→15.5, hard cap 16
    const mapped = 11.5 + Math.log2(Math.max(px, 10) / 10) * 2.2;
    const capped = Math.min(16, Math.max(12, mapped));
    return `font-size: ${capped.toFixed(2)}px`;
  });
}

const baseContentCss = css`
  font-family: Arial, Helvetica, sans-serif;
  line-height: 1.55;
  color: #334155;
  word-wrap: break-word;
  overflow-wrap: anywhere;

  p {
    margin: 0 0 0.75em;
  }
  p:last-child {
    margin-bottom: 0;
  }

  h1, h2, h3, h4, h5, h6 {
    margin: 0.75em 0 0.4em;
    line-height: 1.25;
    font-weight: 700;
    color: inherit;
  }
  h1:first-child, h2:first-child, h3:first-child, h4:first-child {
    margin-top: 0;
  }

  ul, ol {
    margin: 0.55em 0 0.85em;
    padding-left: 1.35em;
  }
  ul {
    list-style: disc;
  }
  ol {
    list-style: decimal;
  }
  ul ul {
    list-style: circle;
  }
  ul ul ul {
    list-style: square;
  }
  ul:last-child,
  ol:last-child {
    margin-bottom: 0;
  }
  li {
    margin: 0.35em 0;
    line-height: 1.5;
  }
  /* Bullets / numbers: brand red (text color can differ via TinyMCE) */
  li::marker {
    color: #d52b1e;
  }
  li > p {
    margin: 0.25em 0;
  }

  strong, b {
    font-weight: 700;
  }
  em, i {
    font-style: italic;
  }
  u {
    text-decoration: underline;
  }
  s, strike, del {
    text-decoration: line-through;
  }
  sub {
    vertical-align: sub;
    font-size: smaller;
  }
  sup {
    vertical-align: super;
    font-size: smaller;
  }

  a {
    color: #043663;
    text-decoration: underline;
  }

  blockquote {
    margin: 0.75em 0;
    padding: 0.35em 0 0.35em 0.9em;
    border-left: 3px solid #cbd5e1;
    color: inherit;
  }

  hr {
    border: none;
    border-top: 1px solid #e2e8f0;
    margin: 1em 0;
  }

  table {
    border-collapse: collapse;
    width: 100%;
    margin: 0.75em 0;
    font-size: inherit;
  }
  th, td {
    border: 1px solid #e2e8f0;
    padding: 0.35em 0.55em;
    vertical-align: top;
  }
  th {
    font-weight: 700;
    background: #f8fafc;
  }

  img, video {
    max-width: 100%;
    height: auto;
  }

  code, pre {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 0.9em;
  }
  pre {
    margin: 0.75em 0;
    padding: 0.65em 0.75em;
    background: #f8fafc;
    border-radius: 6px;
    overflow-x: auto;
  }
`;

const Wrap = styled.div`
  ${baseContentCss}
  font-size: 14px;

  h1 { font-size: 1.5em; }
  h2 { font-size: 1.3em; }
  h3 { font-size: 1.15em; }
  h4, h5, h6 { font-size: 1em; }
`;

/** Compact density for package tiles — keeps cards aligned on desktop. */
const CardWrap = styled.div`
  ${baseContentCss}
  font-size: 0.875rem;
  color: #475569;

  h1, h2, h3, h4, h5, h6 {
    font-size: 1em;
    margin: 0.55em 0 0.3em;
  }

  ul, ol {
    margin: 0.4em 0 0.65em;
  }
  li {
    margin: 0.25em 0;
  }

  /* Mobile rail: allow a bit more size without breaking the tile */
  @media (max-width: 640px) {
    font-size: 0.9rem;
  }
`;

const ClampedCss = css`
  ${(p) =>
    p.$clamped
      ? `
    max-height: ${(p.$lines || 3) * 1.55}em;
    overflow: hidden;
    position: relative;
  `
      : ''}
`;

const ClampedWrap = styled(Wrap)`
  ${ClampedCss}
`;

const ClampedCardWrap = styled(CardWrap)`
  ${ClampedCss}
`;

/**
 * Renders admin product description: TinyMCE HTML (fonts, sizes, colors, lists)
 * or legacy plain text with light markdown-ish formatting.
 *
 * @param {'full'|'card'} density - `card` caps absolute font sizes for package grids
 */
export default function FormattedProductDescription({
  text,
  clamp = false,
  lines = 3,
  className,
  as: As = 'div',
  density = 'full',
}) {
  if (!text || !String(text).trim()) return null;
  let html = formatProductDescriptionHtml(text);
  if (density === 'card') {
    html = normalizeFontsForCard(html);
  }

  const Comp = density === 'card'
    ? clamp
      ? ClampedCardWrap
      : CardWrap
    : clamp
      ? ClampedWrap
      : Wrap;

  return (
    <Comp
      as={As}
      className={className}
      $clamped={clamp}
      $lines={lines}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
